/// <reference types="./eventTracking" />

import { datadogLogs, LogsInitConfiguration } from '@datadog/browser-logs'
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum'
import { IApiClaravineErrorData } from 'api'
import { AxiosError } from 'axios'

import * as pendoAgent from '@pendo/agent'
import * as Sentry from '@sentry/react'
const pendo = pendoAgent as pendo.Pendo

import { Account, User } from '@global/types'
import moment from 'moment'

const enum EventTrackingErrorType {
  GENERIC_ERROR = 'GENERIC_ERROR',
  API_ERROR = 'API_ERROR',
}

export const enum EventTrackingEventType {
  DATA_EXPORTED = 'DATA EXPORTED',
  COLUMNS_SHOWN = 'COLUMNS SHOWN',
  COLUMNS_HIDDEN = 'COLUMNS HIDDEN',
  ROWS_DUPLICATED = 'ROWS DUPLICATED',
  SUBMISSION_SEARCH = 'SUBMISSION SEARCH',
  COLUMNS_FILTERED = 'COLUMNS FILTERED',
  COLUMN_SORTED = 'COLUMN SORTED',
  UPDATE_QUERY_OPTIONS_BY_FIELD_ID = 'UPDATE QUERY OPTIONS BY FIELD ID',
  TAXONOMY_TRANSLATION_REVIEW_MODAL_ACTION = 'TAXONOMY TRANSLATION - REVIEW MODAL ACTION',
  TAXONOMY_TRANSLATION_ITEM_REVIEW_ACTION = 'TAXONOMY TRANSLATION - ITEM REVIEW ACTION',
  INACTIVITY_TIMEOUT = 'INACTIVITY TIMEOUT',
  GEN_AI_FEATURE_REQUESTED = 'GEN AI FEATURE REQUESTED',
  SMART_CORRECT_REQUESTED = 'SMART CORRECT - REQUESTED',
  HOT_TABLE_LOAD_START = 'HoT Table Load Start',
  HOT_TABLE_LOAD_COMPLETE = 'HoT Table Load Complete',
}

export function initEventTracking(user: User, account: Account): void {
  initPendo(user, account)
  initSentry(user.id, account.slug)
  initDatadog(user, account)
}

function initPendo(user: User, currentAccount: Account): void {
  try {
    if (process.env.APP_ENV !== 'production' && process.env.APP_ENV !== 'qa') {
      throw new Error('DEBUG: Pendo is disabled in non-production environments')
    }
    if (typeof window.pendo !== 'undefined') {
      console.info('[PENDO] Already initialized')
      return
    }

    const { id, created_at, email, name } = user
    const createdAt = moment(created_at)
    const daysSinceCreation = moment().diff(createdAt, 'days')

    pendo.initialize({
      apiKey: '73b4b62e-5821-401d-6687-466b83a993f0',
      env: 'io',
      visitor: {
        id: String(id),
        email,
        name,
        created_at,
        role: user.role,
        is_super_user: Boolean(user.super_admin),
        days_since_creation: daysSinceCreation,
        version: 'KALO',
      },
      account: {
        id: String(currentAccount.id),
        name: currentAccount.name,
        app_env: process.env.APP_ENV,
      },
    })
  } catch (e) {
    console.info('[PENDO] Failed to initialize', e)
  }
}

function initSentry(userId: number, slug: string): void {
  try {
    if (process.env.APP_ENV !== 'production' && process.env.APP_ENV !== 'qa') {
      throw new Error('DEBUG: Sentry is disabled in non-production environments')
    }

    Sentry.init({
      dsn: 'https://6e9a106c3a8240feb4344b61d70a0c31@o246778.ingest.sentry.io/5380376',
      environment: process.env.APP_ENV,
      // eslint-disable-next-line lingui/no-unlocalized-strings
      ignoreErrors: ['ResizeObserver loop limit exceeded'],
    })

    const scope = Sentry.getCurrentScope()
    scope.setUser({ id: userId })
    scope.setTag('account', slug)
  } catch (e) {
    console.info('[SENTRY] Failed to initialize', e)
  }
}

function initDatadog(user: User, currentAccount: Account): void {
  try {
    if (process.env.APP_ENV !== 'production' && process.env.APP_ENV !== 'qa') {
      throw new Error('DEBUG: Datadog RUM is disabled in non-production environments')
    }

    type DatadogConfig = Pick<
      RumInitConfiguration & LogsInitConfiguration,
      | 'clientToken'
      | 'site'
      | 'service'
      | 'allowedTrackingOrigins'
      | 'env'
      | 'sessionSampleRate'
      | 'sessionReplaySampleRate'
    >
    const datadogConfig: DatadogConfig = {
      clientToken: window.KALO_DATADOG_RUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'claravine',
      allowedTrackingOrigins: [/https:\/\/.*\.claravine\.com/],
      env: process.env.APP_ENV,
      sessionSampleRate: window.KALO_DATADOG_RUM_SAMPLE_RATE,
      sessionReplaySampleRate: window.KALO_DATADOG_RUM_SAMPLE_RATE,
    }

    datadogLogs.init({
      ...datadogConfig,
      forwardErrorsToLogs: true,
    })

    datadogRum.init({
      ...datadogConfig,
      applicationId: window.KALO_DATADOG_RUM_APP_ID,
      trackUserInteractions: true,
      actionNameAttribute: 'data-event-name',
      defaultPrivacyLevel: 'allow',
    })
    datadogRum.setUser({
      id: user.id.toString(),
      role: user.role,
      accountId: currentAccount.id,
      accountName: currentAccount.name,
    })
    datadogRum.startSessionReplayRecording()
  } catch (e) {
    console.info('[DATADOG] RUM failed to initialize', e)
  }
}

export function trackEvent(
  message: EventTrackingEventType,
  details: Record<string, string | number | boolean | string[] | object | null> = {},
) {
  if (process.env.APP_ENV !== 'production' && process.env.APP_ENV !== 'qa') {
    console.info(
      '[EVENT TRACKING] Skipping event tracking in non-production environment',
      message,
      details,
    )
    return
  }

  const scope = Sentry.getCurrentScope()
  scope.setLevel('info')
  Sentry.captureMessage(message, {
    extra: details,
  })

  if (typeof window.pendo !== 'undefined') {
    window.pendo.track(message, details as pendo.Metadata) // yes, after inititalizing pendo, it puts a variable on Window which is what needs to be called....
  } else {
    Sentry.captureMessage('Pendo unexpectedly not initialized')
  }

  datadogRum.addAction(message, details)
}

export function trackWarning(message: string, details: Record<string, unknown> = {}) {
  if (process.env.APP_ENV !== 'production' && process.env.APP_ENV !== 'qa') {
    console.warn(
      '[EVENT TRACKING] Skipping event tracking in non-production environment',
      message,
      details,
    )
    return
  }

  const scope = Sentry.getCurrentScope()
  scope.setLevel('warning')

  Sentry.captureMessage(message, {
    extra: details,
  })

  datadogLogs.logger.warn(message, details)
}

export function trackError(error: Error, details: Record<string, unknown> = {}) {
  if (process.env.APP_ENV !== 'production' && process.env.APP_ENV !== 'qa') {
    console.error(
      '[EVENT TRACKING] Skipping event tracking in non-production environment',
      error,
      details,
    )
    return
  }

  // eslint-disable-next-line lingui/no-unlocalized-strings
  let message = 'Unknown error'
  let typedErrorMessage: string | undefined
  let path: string | undefined
  let status: number | undefined
  let contextKey: EventTrackingErrorType = EventTrackingErrorType.GENERIC_ERROR
  const context: Record<string, unknown> = {}
  const scope = Sentry.getCurrentScope()
  scope.setLevel('error')

  if (error instanceof AxiosError) {
    const typedError = error as AxiosError<IApiClaravineErrorData | undefined>
    if (typedError.response) {
      // TODO: what is the real shape of the data object returned by the API for an error?
      ;({ error: typedErrorMessage, path, status } = typedError.response.data ?? {})
      context.path = path
    }

    message = typedErrorMessage ?? error.message
    context.error = { ...error.toJSON() }

    scope.setTag('api_status', status)
    contextKey = EventTrackingErrorType.API_ERROR
  }

  scope.setContext(contextKey, {
    ...context,
  })
  scope.setExtras({
    ...details,
  })
  Sentry.captureException(message)

  datadogLogs.logger.error(message, details, error)
}
